import { CountryStateCodes } from "@taabl/common-lib/constants/CountryStateCodes";
import { checkForNumbers } from "@taabl/common-lib/utils";
import { Form, Formik } from "formik";
import moment from "moment";
import React, { useState } from "react";
import { Button, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaInfoCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import * as yup from "yup";
import FormikDropdown from "../../components/Forms/FormikDropdown";
import FormikTextField from "../../components/Forms/FormikTextField";
import LoaderButton from "../../components/LoaderButton";
import PageLoader from "../../components/PageLoader";
import { Constants } from "../../libs/Constants";
import { update } from "../../libs/dbFunctions";
import { getCurrentDate, getValidDate } from "../../libs/utils";
import useEmployeeContext from "./contexts/useEmployeeContext";
const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  address: "",
  country: "USA",
  city: "",
  state: "",
  zip: "",
  dateOfBirth: "",
  ssn: "",
  phoneNumber: "",
};
const schema = yup.object({
  email: yup
    .string()
    .required("A valid email is required")
    .email("A valid email is required"),
  address: yup.string().required("Address is required"),
  city: yup.string().required("City is required"),
  zip: yup.string().required("Zip is required"),
  state: yup.string().required("State is required"),
  country: yup.string().required("Country is required"),
  dateOfBirth: yup
    .string()
    .required("Date Of Birth is required")
    .test("dateOfBirth", function (value) {
      let datevalidity = moment(
        value,
        [
          Constants.DATEFORMAT.MMDDYYYY,
          Constants.DATEFORMAT.MDDYYYY,
          Constants.DATEFORMAT.MMDYYYY,
          Constants.DATEFORMAT.MDYYYY,
        ],
        true
      );
      if (!datevalidity.isValid()) {
        return this.createError({
          message:
            "Please enter your Date Of Birth in the correct format (MM/DD/YYYY).",
          path: "dateOfBirth",
        });
      }
      let dob = getValidDate(value, false);
      let currentDate = getCurrentDate(false);
      if (currentDate.diff(dob, "years") < 18) {
        return this.createError({
          message: "User should be older than 18 years.",
          path: "dateOfBirth",
        });
      }
      if (currentDate.diff(dob, "years") > 125) {
        return this.createError({
          message: "User should be younger than 125 years.",
          path: "dateOfBirth",
        });
      }
      return true;
    }),
  ssn: yup
    .string()
    .required("All 9 digits of the SSN are required.")
    .test("ssn", function (value) {
      if (value && !checkForNumbers(value)) {
        return this.createError({
          message: "Please enter only numbers.",
          path: "ssn",
        });
      }
      if (this.parent.country === "USA" && value && value.length !== 9) {
        return this.createError({
          message: "SSN should be 9 digits long.",
          path: "ssn",
        });
      }
      return true;
    }),
  phoneNumber: yup
    .string()
    .required("Phone number is required")
    .matches(/^[^|]/, "Invalid Phone Number"),
});

export default function ReverifyCustomer() {
  const { employee, refreshEmployee } = useEmployeeContext();
  const [showCustomerReverify, setShowCustomerReverify] = useState(false);
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (values) => {
    setLoading(true);
    let saveFields = {
      ...employee,
      ...values,
      reverifyUser: true
    };
    //update the userObj with address details
    try {
      await update(Constants.dbTypes.USER, saveFields);
      toast.success("Account reverification details submitted successfully");
      refreshEmployee();
      setLoading(false);
      setShowCustomerReverify(false);
    } catch (e) {
      if (e.response && e.response.data && e.response.data.error) {
        toast.error(e.response.data.error);
      } else {
        toast.error(e);
      }
    }
  };

  const renderReverifyCustomerModal = () => {
    console.log('renderReverifyCustomerModal');
    return (
      <Modal
        show={showCustomerReverify}
        onHide={() => setShowCustomerReverify(false)}
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="contribution-modal font-secondary"
      >
        <Modal.Header className="p-4" closeButton>
          <Modal.Title>
            There was an issue verifying your identity. Please fill out the form
            below to verify your identity.{" "}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            validationSchema={schema}
            onSubmit={handleSubmit}
            initialValues={initialValues}
          >
            {({ isValid, isSubmitting, values }) => (
              <Form noValidate className="w-100">
                <FormikTextField
                  size="lg"
                  controlId="firstName"
                  label="First Name"
                  type="text"
                  name="firstName"
                  className="mb-4"
                />
                <FormikTextField
                  size="lg"
                  controlId="lastName"
                  label="Last Name"
                  type="text"
                  name="lastName"
                  className="mb-4"
                />
                <FormikTextField
                  size="lg"
                  controlId="email"
                  label="Email"
                  type="text"
                  name="email"
                  className="mb-4"
                />
                <FormikTextField
                  size="lg"
                  controlId="city"
                  label="City"
                  type="text"
                  name="city"
                  className="mb-4"
                />
                <FormikDropdown
                  options={CountryStateCodes}
                  placeholder="Select Country"
                  size="lg"
                  controlId="country"
                  label="Country"
                  type="text"
                  name="country"
                  className="mb-4"
                  optionVal="iso3"
                />
                <FormikDropdown
                  size="lg"
                  options={
                    values.country !== "" && values.country !== "Select Country"
                      ? CountryStateCodes.filter(
                        (c) => c.iso3 === values.country
                      )[0].states
                      : []
                  }
                  placeholder="Select State"
                  controlId="state"
                  label="State"
                  type="text"
                  name="state"
                  className="mb-4"
                  optionVal="state_code"
                />
                <FormikTextField
                  size="lg"
                  controlId="address"
                  label="Address"
                  type="text"
                  name="address"
                  className="mb-4"
                />
                <FormikTextField
                  size="lg"
                  controlId="zip"
                  label="Zip"
                  type="text"
                  name="zip"
                  className="mb-4"
                />
                <FormikTextField
                  size="lg"
                  controlId="dateOfBirth"
                  label="Date Of Birth"
                  type="text"
                  name="dateOfBirth"
                  className="mb-4"
                  placeholder={Constants.DATEFORMAT.MMDDYYYY}
                />
                <FormikTextField
                  size="lg"
                  controlId="ssn"
                  label="SSN (All 9 digits)"
                  type="text"
                  name="ssn"
                  className="mb-4"
                  labelAppend={
                    <OverlayTrigger
                      placement={"top"}
                      delay={{ show: 150, hide: 300 }}
                      overlay={
                        <Tooltip style={{ zIndex: 9999999999 }}>
                          All digits of SSN are required to re-verify your
                          account.
                        </Tooltip>
                      }
                    ><span>
                        <FaInfoCircle
                          style={{
                            marginLeft: "5px",
                          }}
                        />
                      </span>

                    </OverlayTrigger>
                  }
                />
                <FormikTextField
                  size="lg"
                  controlId="phoneNumber"
                  label="Phone number"
                  type="text"
                  name="phoneNumber"
                  className="mb-4"
                />
                <LoaderButton
                  block
                  type="submit"
                  variant="primary"
                  size="lg"
                  text="Next"
                  className="mb-2"
                  isLoading={isSubmitting}
                  disabled={!isValid || isSubmitting}
                >
                  Continue
                </LoaderButton>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    );
  };
  return (
    <PageLoader loading={loading}>
      <Button
        onClick={() => {
          setShowCustomerReverify(true);
        }}
      >
        Reverify Customer
      </Button>
      {showCustomerReverify && renderReverifyCustomerModal()}
    </PageLoader>
  );
}
